import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import DoNotDisturbIcon from "@mui/icons-material/DoNotDisturb";
import {
  GoogleMap,
  InfoWindow,
  Marker,
  useLoadScript,
} from "@react-google-maps/api";
import { statecityData } from "../../../shared/enums";
import getAddressLatLong from "../../../shared/utils/getAddressLatLong";
import GlobalButton from "../../../shared/utils/GlobalButton";
import { Slider } from "@mui/material";
import { Circle } from "@react-google-maps/api";
// ─── Map constants ─────────────────────────────────────────────────────────────
const MAP_CONTAINER_STYLE = { width: "100%", height: "450px" };
const INDIA_CENTER = { lat: 20.5937, lng: 78.9629 };
const LIBRARIES = ["places"];
const PIN_PATH =
  "M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z" +
  "m0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z";

// ─── Default form ──────────────────────────────────────────────────────────────
const DEFAULT_FORM = {
  cityName: "",
  stateName: "",
  remarks: "",
  isActive: true,
};

// ─── Normalise whatever getAddressLatLong returns → { lat, lng } | null ───────
// The utility can return { lat, lng } or { latitude, longitude } depending on
// which geocoder is used under the hood. We normalise once here.
const normaliseCoords = (raw) => {
  if (!raw) return null;
  const lat = parseFloat(raw.lat ?? raw.latitude);
  const lng = parseFloat(raw.lng ?? raw.longitude);
  if (isNaN(lat) || isNaN(lng) || (lat === 0 && lng === 0)) return null;
  return { lat, lng };
};

/**
 * Props
 *   open       {boolean}
 *   onClose    {() => void}
 *   onSave     {(payload) => void}
 *   editData   {object|null}  null → create; object → edit
 *                             editData.linkedCities = [{ _id, cityName, stateName, coordinates }]
 *   isSaving   {boolean}
 */
const ViewDeliveryCenterModal = ({
  open,
  onClose,
  onSave,
  editData = null,
  isSaving = false,
}) => {
  const isEditMode = Boolean(editData);
  const isFirstRender = useRef(true);
  const stateFieldRef = useRef(null);
  const cityFieldRef = useRef(null);
  const dialogContentRef = useRef(null);
  const [formData, setFormData] = useState(DEFAULT_FORM);
  const [cityArray, setCityArray] = useState([]);
  const [cityLoading, setCityLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [errors, setErrors] = useState({});
  const [submitError, setSubmitError] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [linkedCityNames, setLinkedCityNames] = useState([]);
  const [selectedMarker, setSelectedMarker] = useState(null);
  const [radiusKm, setRadiusKm] = useState(50);
  const [allLinkedCities, setAllLinkedCities] = useState([]);
  const [initialLinkedCities, setInitialLinkedCities] = useState([]);
  const [hasUserInteractedWithRadius, setHasUserInteractedWithRadius] =
    useState(false);
  // ── resolvedCoords: { [cityName]: { lat, lng } } ──────────────────────────
  // Single source of truth for all city coordinates inside this modal.
  // Populated from three sources (cheapest first):
  //   1. countrystatecity API lat/lng   (free, instant, but often null for India)
  //   2. editData stored coordinates    (already in DB — seeded on open)
  //   3. getAddressLatLong geocoding    (accurate fallback, called only when needed)
  const [resolvedCoords, setResolvedCoords] = useState({});

  // Track which city names are currently being geocoded so we never fire the
  // same geocode call twice (e.g. user rapidly checks/unchecks a city).
  const geocodingRef = useRef(new Set());

  const cityApiKey = process.env.REACT_APP_FETCH_STATE_CITY_API_KEY;
  const { isLoaded, loadError } = useLoadScript({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAP_API_KEY,
    libraries: LIBRARIES,
  });

  // ── Reset function ────────────────────────────────────────────────────────────
  const resetModalState = useCallback(() => {
    setIsProcessing(false);
    setSubmitError(null);
    setSearch("");
    setErrors({});
    setSelectedMarker(null);
    setHasUserInteractedWithRadius(false);
    geocodingRef.current.clear();

    if (!isEditMode) {
      setFormData(DEFAULT_FORM);
      setLinkedCityNames([]);
      setAllLinkedCities([]);
      setInitialLinkedCities([]);
      setResolvedCoords({});
      setRadiusKm(50);
    }
  }, [isEditMode]);

  // ── Cleanup when modal closes ──────────────────────────────────────────────
  useEffect(() => {
    if (!open) {
      resetModalState();
    }
  }, [open, resetModalState]);

  // ── Populate form when modal opens ────────────────────────────────────────
  useEffect(() => {
    if (!open) return;

    isFirstRender.current = true;
    setHasUserInteractedWithRadius(false);
    setSubmitError(null);
    setIsProcessing(false);

    if (editData) {
      setFormData({
        cityName: editData.cityName || "",
        stateName: editData.stateName || "",
        remarks: editData.remarks || "",
        isActive: editData.isActive ?? true,
      });

      setRadiusKm(editData.radiusKm || 50);

      const allLinked = (editData.linkedCities || []).map(
        (city) => city.cityName,
      );
      setAllLinkedCities(allLinked);
      setInitialLinkedCities([...allLinked]);
      setLinkedCityNames([...allLinked]);

      const seeded = {};
      const dcStored = editData.coordinates;
      if (dcStored?.latitude && dcStored?.longitude) {
        seeded[editData.cityName] = {
          lat: dcStored.latitude,
          lng: dcStored.longitude,
        };
      }
      for (const link of editData.linkedCities || []) {
        const c = link.coordinates;
        if (c?.latitude && c?.longitude) {
          seeded[link.cityName] = { lat: c.latitude, lng: c.longitude };
        }
      }
      setResolvedCoords(seeded);
    } else {
      setFormData(DEFAULT_FORM);
      setLinkedCityNames([]);
      setAllLinkedCities([]);
      setInitialLinkedCities([]);
      setResolvedCoords({});
      setRadiusKm(50);
    }

    setSearch("");
    setErrors({});
    setCityArray([]);
    setSelectedMarker(null);
    geocodingRef.current.clear();
  }, [open, editData]);

  const availableCities = useMemo(
    () =>
      cityArray
        .map((c) => c.name)
        .filter(
          (name) =>
            name !== formData.cityName &&
            name.toLowerCase().includes(search.toLowerCase()),
        ),
    [cityArray, formData.cityName, search],
  );

  useEffect(() => {
    if (isEditMode && isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }

    const dcCoord = resolvedCoords[formData.cityName];

    if (!dcCoord || !formData.cityName || cityArray.length === 0) return;

    const citiesInsideRadius = [];

    for (const city of cityArray) {
      if (city.name === formData.cityName) continue;

      const cityCoord = resolvedCoords[city.name];

      if (!cityCoord) continue;

      const distance = getDistanceKm(
        dcCoord.lat,
        dcCoord.lng,
        cityCoord.lat,
        cityCoord.lng,
      );

      if (distance <= radiusKm) {
        citiesInsideRadius.push(city.name);
      }
    }

    setLinkedCityNames((prev) => {
      if (isEditMode) {
        if (!hasUserInteractedWithRadius) {
          return [...allLinkedCities];
        }

        const preloadedSet = new Set(allLinkedCities);

        const result = [...allLinkedCities];

        for (const city of citiesInsideRadius) {
          if (!preloadedSet.has(city) && !result.includes(city)) {
            result.push(city);
          }
        }

        return result.filter((city) => {
          if (preloadedSet.has(city)) return true;
          return citiesInsideRadius.includes(city);
        });
      }

      const manuallySelected = prev.filter(
        (city) => !availableCities.includes(city),
      );
      const result = [...manuallySelected];
      for (const city of citiesInsideRadius) {
        if (!result.includes(city)) {
          result.push(city);
        }
      }

      return result.filter((city) => {
        if (manuallySelected.includes(city)) return true;
        return citiesInsideRadius.includes(city);
      });
    });
  }, [
    radiusKm,
    formData.cityName,
    resolvedCoords,
    cityArray,
    isEditMode,
    allLinkedCities,
    hasUserInteractedWithRadius,
    availableCities,
  ]);

  // ── Fetch city list when state changes ────────────────────────────────────
  useEffect(() => {
    if (!formData.stateName) {
      setCityArray([]);
      return;
    }
    const stateCode = statecityData.find(
      (s) => s.name === formData.stateName,
    )?.iso2;
    if (!stateCode) return;

    const run = async () => {
      setCityLoading(true);
      try {
        const headers = new Headers();
        headers.append("X-CSCAPI-KEY", cityApiKey);
        const res = await fetch(
          `https://api.countrystatecity.in/v1/countries/IN/states/${stateCode}/cities`,
          { method: "GET", headers, redirect: "follow" },
        );
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        setCityArray(data);

        data.forEach((city) => {
          if (!resolvedCoords[city.name]) {
            geocodeCity(city.name, formData.stateName);
          }
        });
        // Opportunistically populate resolvedCoords from the API response.
        // Only write cities we don't already know (DB value wins in edit mode).
        setResolvedCoords((prev) => {
          const next = { ...prev };
          for (const c of data) {
            if (c.name in next) continue; // already resolved
            const coords = normaliseCoords({
              lat: c.latitude,
              lng: c.longitude,
            });
            if (coords) next[c.name] = coords;
          }
          return next;
        });
      } catch (err) {
        console.error("Error fetching cities:", err);
        setCityArray([]);
      } finally {
        setCityLoading(false);
      }
    };
    if (open) {
      run();
    }
  }, [formData.stateName, open]);

  // ── Core geocoding function ────────────────────────────────────────────────
  // Resolves coordinates for a single city name and writes them into
  // resolvedCoords.  Guards against duplicate in-flight calls via geocodingRef.
  const geocodeCity = useCallback(
    async (cityName, stateName) => {
      if (!cityName || !stateName) return;
      if (geocodingRef.current.has(cityName)) return; // already in-flight
      if (resolvedCoords[cityName]) return; // already resolved

      geocodingRef.current.add(cityName);
      try {
        const raw = await getAddressLatLong(cityName, stateName);
        const coords = normaliseCoords(raw);
        setResolvedCoords((prev) => ({ ...prev, [cityName]: coords }));
      } catch (err) {
        console.error(`Geocode failed for "${cityName}":`, err);
        setResolvedCoords((prev) => ({ ...prev, [cityName]: null }));
      } finally {
        geocodingRef.current.delete(cityName);
      }
    },
    // resolvedCoords intentionally omitted: we read it at call-time via the guard above
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [resolvedCoords],
  );

  // ── Geocode the DC city whenever it changes ────────────────────────────────
  useEffect(() => {
    if (!formData.cityName || !formData.stateName) return;
    // Clear any stale coords that belonged to a previous city selection so the
    // DC marker moves immediately when the user picks a different city.
    setResolvedCoords((prev) => {
      // Remove old DC city's coords only if it was never part of editData
      // (we want to keep coords that came from DB on the first render).
      const oldCity = editData?.cityName;
      if (
        formData.cityName !== oldCity &&
        oldCity &&
        prev[oldCity] &&
        !editData?.coordinates?.latitude
      ) {
        const next = { ...prev };
        delete next[oldCity];
        return next;
      }
      return prev;
    });
    geocodeCity(formData.cityName, formData.stateName);
  }, [formData.cityName, formData.stateName, geocodeCity, editData]);

  // ── Geocode any newly-toggled linked city ─────────────────────────────────
  // We diff the previous list against the new one inside toggleCity (below)
  // rather than watching linkedCityNames here, to avoid mass-geocoding
  // the full list every time one city is checked.
  const toggleCity = useCallback(
    (cityName) => {
      setLinkedCityNames((prev) => {
        if (prev.includes(cityName)) {
          return prev.filter((c) => c !== cityName);
        }
        // City is being added — kick off geocoding in the background
        if (!resolvedCoords[cityName]) {
          geocodeCity(cityName, formData.stateName);
        }
        return [...prev, cityName];
      });
    },
    [geocodeCity, resolvedCoords, formData.stateName],
  );

  const mapMarkers = useMemo(() => {
    const markers = [];

    // DC marker (blue)
    const dcCoord = resolvedCoords[formData.cityName];
    if (formData.cityName && dcCoord) {
      markers.push({
        name: formData.cityName,
        lat: dcCoord.lat,
        lng: dcCoord.lng,
        isDC: true,
      });
    }

    // Linked city markers (red) — only cities whose coords are already resolved
    for (const cityName of linkedCityNames) {
      const c = resolvedCoords[cityName];
      if (c)
        markers.push({ name: cityName, lat: c.lat, lng: c.lng, isDC: false });
    }

    return markers;
  }, [formData.cityName, linkedCityNames, resolvedCoords]);

  // ── Map center: midpoint of all visible markers ───────────────────────────
  const mapCenter = useMemo(() => {
    if (mapMarkers.length === 0) return INDIA_CENTER;
    const lat = mapMarkers.map((m) => m.lat);
    const lng = mapMarkers.map((m) => m.lng);
    return {
      lat: (Math.min(...lat) + Math.max(...lat)) / 2,
      lng: (Math.min(...lng) + Math.max(...lng)) / 2,
    };
  }, [mapMarkers]);

  // ── How many selected cities are still being geocoded ─────────────────────
  const pendingCount = useMemo(() => {
    const allSelected = [...linkedCityNames];
    if (formData.cityName) allSelected.push(formData.cityName);
    return allSelected.filter((n) => geocodingRef.current.has(n)).length;
  }, [linkedCityNames, formData.cityName]); // resolvedCoords as proxy for ref changes

  // ── Handlers ──────────────────────────────────────────────────────────────
  const handleChange = (field) => (e) => {
    setFormData((prev) => ({ ...prev, [field]: e.target.value }));
    if (errors[field]) setErrors((prev) => ({ ...prev, [field]: "" }));
    if (submitError) setSubmitError(null);
  };

  const handleRadiusChange = (_, value) => {
    setHasUserInteractedWithRadius(true);
    setRadiusKm(value);
  };

  // ── Validation ─────────────────────────────────────────────────────────────
  const validate = () => {
    const errs = {};
    if (!formData.stateName) errs.stateName = "State is required.";
    if (!formData.cityName) errs.cityName = "City is required.";
    setErrors(errs);

    if (errs.stateName) {
      setTimeout(() => {
        if (stateFieldRef.current) {
          try {
            stateFieldRef.current.focus();
            stateFieldRef.current.scrollIntoView({
              behavior: "smooth",
              block: "center",
            });
          } catch (e) {}
        } else {
          dialogContentRef.current?.scrollTo({ top: 0, behavior: "smooth" });
        }
      }, 50);
    } else if (errs.cityName) {
      setTimeout(() => {
        if (cityFieldRef.current) {
          try {
            cityFieldRef.current.focus();
            cityFieldRef.current.scrollIntoView({
              behavior: "smooth",
              block: "center",
            });
          } catch (e) {}
        }
      }, 50);
    }

    return Object.keys(errs).length === 0;
  };

  // ── Submit ─────────────────────────────────────────────────────────────────
  const handleSubmit = async () => {
    if (!validate()) return;

    setSubmitError(null);
    setIsProcessing(true);

    // Build { [cityName]: { latitude, longitude } } for parent → linkCity API
    const linkedCityCoords = {};
    for (const name of linkedCityNames) {
      const c = resolvedCoords[name];
      if (c) linkedCityCoords[name] = { latitude: c.lat, longitude: c.lng };
    }

    const dcResolved = resolvedCoords[formData.cityName];
    const dcCoord = dcResolved
      ? { latitude: dcResolved.lat, longitude: dcResolved.lng }
      : undefined;

    try {
      await onSave({
        ...formData,
        radiusKm: radiusKm,
        coordinates: dcCoord,
        linkedCities: linkedCityNames,
        linkedCityCoords,
      });
      setIsProcessing(false);
      onClose();
    } catch (error) {
      setIsProcessing(false);
      setSubmitError(error?.message || "Failed to save delivery center");
      console.error("Save error:", error);
      dialogContentRef.current?.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  const savedLinkedCount = allLinkedCities.length;
  const currentSelectedCount = linkedCityNames.length;

  const getCityCountDisplay = useMemo(() => {
    if (!isEditMode) {
      return `${currentSelectedCount} cities have been selected`;
    }

    const initialSet = new Set(initialLinkedCities);
    const currentSet = new Set(linkedCityNames);

    const addedCities = linkedCityNames.filter((city) => !initialSet.has(city));
    const removedCities = initialLinkedCities.filter(
      (city) => !currentSet.has(city),
    );

    const addedCount = addedCities.length;
    const removedCount = removedCities.length;

    if (addedCount === 0 && removedCount === 0) {
      return `${savedLinkedCount} cities currently linked`;
    }

    let displayText = `${savedLinkedCount} cities currently linked`;
    const parts = [];

    if (addedCount > 0) {
      parts.push(`${addedCount} more selected`);
    }

    if (removedCount > 0) {
      parts.push(`${removedCount} removed from selection`);
    }

    if (parts.length > 0) {
      displayText += ` (${parts.join(", ")})`;
    }

    return displayText;
  }, [
    isEditMode,
    initialLinkedCities,
    linkedCityNames,
    savedLinkedCount,
    currentSelectedCount,
  ]);

  // ── Marker icon factory ────────────────────────────────────────────────────
  const makeIcon = (isDC) => ({
    path: PIN_PATH,
    fillColor: isDC ? "#1565C0" : "#D32F2F",
    fillOpacity: 1,
    strokeColor: "#FFFFFF",
    strokeWeight: 1.5,
    scale: 2,
    anchor: new window.google.maps.Point(12, 24),
  });

  if (loadError)
    return (
      <Box p={4} textAlign="center" color="error.main">
        Failed to load Google Maps. Check your API key.
      </Box>
    );

  const isSavingState = isSaving || isProcessing;

  return (
    <Dialog
      open={open}
      onClose={(event, reason) => {
        if (reason === "backdropClick" && (submitError || isSavingState))
          return;
        if (!isSavingState) {
          resetModalState();
          onClose();
        }
      }}
      maxWidth={false}
      PaperProps={{
        sx: {
          width: "95vw",
          maxWidth: "1700px",
          height: "90vh",
          borderRadius: 3,
        },
      }}
    >
      <DialogContent ref={dialogContentRef} sx={{ p: 4, overflowY: "auto" }}>
        {/* ── Header ─────────────────────────────────────────────────────── */}
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mb={4}
        >
          <Typography variant="h4" fontWeight={700}>
            {isEditMode ? "Edit Delivery Center" : "Create Delivery Center"}
          </Typography>
          <IconButton
            onClick={() => {
              if (!isSavingState) {
                resetModalState();
                onClose();
              }
            }}
            disabled={isSavingState}
          >
            <CloseIcon />
          </IconButton>
        </Box>

        {submitError && (
          <Alert
            severity="error"
            sx={{ mb: 3 }}
            onClose={() => setSubmitError(null)}
          >
            {submitError}
          </Alert>
        )}

        {/* ── DC Details ─────────────────────────────────────────────────── */}
        <Paper variant="outlined" sx={{ p: 4, borderRadius: 3, mb: 4 }}>
          <Typography variant="h5" fontWeight={700} mb={4}>
            {isEditMode
              ? `Delivery Center : ${editData?.cityName || ""}`
              : "New Delivery Center"}
          </Typography>

          <Grid container spacing={3}>
            {/* State */}
            <Grid item xs={12} md={4}>
              <Autocomplete
                options={statecityData}
                getOptionLabel={(o) => o.name}
                value={
                  statecityData.find((s) => s.name === formData.stateName) ||
                  null
                }
                onChange={(_, val) => {
                  setFormData((prev) => ({
                    ...prev,
                    stateName: val?.name || "",
                    cityName: "",
                  }));
                  if (!isEditMode) {
                    setLinkedCityNames([]);
                    setAllLinkedCities([]);
                    setInitialLinkedCities([]);
                    setResolvedCoords({});
                    geocodingRef.current.clear();
                  }
                  if (errors.stateName)
                    setErrors((p) => ({ ...p, stateName: "" }));
                  if (submitError) setSubmitError(null);
                }}
                disabled={isEditMode}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    inputRef={stateFieldRef}
                    label="State *"
                    fullWidth
                    error={Boolean(errors.stateName)}
                    helperText={errors.stateName || ""}
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {isEditMode && (
                            <Box
                              component="span"
                              sx={{
                                opacity: 0,
                                transition: "opacity 0.2s ease-in-out",
                                display: "inline-flex",
                                mr: 1,
                                "&:hover": {
                                  opacity: 1,
                                },
                                ".MuiTextField-root:hover &, .MuiAutocomplete-root:hover &":
                                  {
                                    opacity: 1,
                                  },
                              }}
                            >
                              <DoNotDisturbIcon
                                sx={{
                                  color: "action.disabled",
                                  fontSize: 20,
                                }}
                              />
                            </Box>
                          )}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />
            </Grid>

            {/* DC City */}
            <Grid item xs={12} md={4}>
              <Autocomplete
                options={cityArray}
                getOptionLabel={(o) => o.name}
                value={
                  cityArray.find((c) => c.name === formData.cityName) || null
                }
                onChange={(_, val) => {
                  const newCity = val?.name || "";
                  setFormData((prev) => ({ ...prev, cityName: newCity }));
                  // Remove the selected DC city from linked list if it was there
                  if (newCity && !isEditMode) {
                    setLinkedCityNames((prev) =>
                      prev.filter((c) => c !== newCity),
                    );
                  }
                  if (errors.cityName)
                    setErrors((p) => ({ ...p, cityName: "" }));
                  if (submitError) setSubmitError(null);
                }}
                loading={cityLoading}
                disabled={isEditMode || !formData.stateName}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    inputRef={cityFieldRef}
                    label="Delivery Center City *"
                    fullWidth
                    error={Boolean(errors.cityName)}
                    helperText={
                      errors.cityName ||
                      (!formData.stateName ? "Select a state first" : "")
                    }
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {isEditMode && (
                            <Box
                              component="span"
                              sx={{
                                opacity: 0,
                                transition: "opacity 0.2s ease-in-out",
                                display: "inline-flex",
                                mr: 1,
                                "&:hover": {
                                  opacity: 1,
                                },
                                ".MuiTextField-root:hover &, .MuiAutocomplete-root:hover &":
                                  {
                                    opacity: 1,
                                  },
                              }}
                            >
                              <DoNotDisturbIcon
                                sx={{
                                  color: "action.disabled",
                                  fontSize: 20,
                                }}
                              />
                            </Box>
                          )}
                          {cityLoading && (
                            <CircularProgress color="inherit" size={18} />
                          )}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />
            </Grid>

            {/* Status */}
            <Grid item xs={12} md={4}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  label="Status"
                  value={formData.isActive ? "active" : "inactive"}
                  onChange={(e) => {
                    setFormData((prev) => ({
                      ...prev,
                      isActive: e.target.value === "active",
                    }));
                    if (submitError) setSubmitError(null);
                  }}
                >
                  <MenuItem value="active">Active</MenuItem>
                  <MenuItem value="inactive">Inactive</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {/* Remarks */}
            <Grid item xs={12}>
              <TextField
                label="Remarks"
                fullWidth
                multiline
                rows={3}
                value={formData.remarks}
                onChange={handleChange("remarks")}
              />
            </Grid>
          </Grid>
        </Paper>

        {/* ── Linked Cities + Map ─────────────────────────────────────────── */}
        <Grid container spacing={3}>
          {/* Left: Linked Cities panel */}
          <Grid item xs={12} md={7}>
            <Paper
              variant="outlined"
              sx={{ p: 4, borderRadius: 3, height: "100%" }}
            >
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                mb={1}
              >
                <Typography variant="h5" fontWeight={700}>
                  Linked Cities
                </Typography>
              </Box>

              <Typography color="text.secondary" mb={3}>
                {isEditMode
                  ? `Currently ${savedLinkedCount} cities are linked to this delivery center.
                  Select/deselect cities below to update the list.`
                  : `Select cities in ${formData.stateName || "the selected state"}
                  that will be managed by this delivery center.`}
              </Typography>

              {!formData.cityName && (
                <Alert severity="info" sx={{ mb: 2 }}>
                  Please select a state and a delivery center city first to
                  manage linked cities.
                </Alert>
              )}

              {/* Search + selected chips row */}
              <Box
                display="flex"
                alignItems="flex-start"
                gap={2}
                flexWrap="wrap"
              >
                <TextField
                  placeholder="Search cities…"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  disabled={!formData.cityName}
                  size="small"
                  sx={{ flexShrink: 0, width: 220 }}
                  InputProps={{
                    startAdornment: (
                      <SearchIcon sx={{ mr: 1, color: "text.secondary" }} />
                    ),
                  }}
                />
                <Box
                  flex={1}
                  display="flex"
                  gap={1}
                  flexWrap="wrap"
                  alignItems="center"
                >
                  {linkedCityNames.length === 0 ? (
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mt: 0.5 }}
                    >
                      No cities selected yet.
                    </Typography>
                  ) : (
                    // linkedCityNames.map((city) => (
                    //   <Chip
                    //     key={city}
                    //     label={city}
                    //     onDelete={() => toggleCity(city)}
                    //     color="primary"
                    //     variant="outlined"
                    //     size="small"
                    //   />
                    // ))
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mt: 0.5 }}
                    >
                      {getCityCountDisplay}
                    </Typography>
                  )}
                </Box>
              </Box>

              {/* Available cities grid */}
              <Box mt={3}>
                <Box
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                  mb={1}
                >
                  <Typography fontWeight={600} variant="body2">
                    Available Cities
                    {availableCities.length
                      ? ` (${availableCities.length})`
                      : ""}
                  </Typography>
                  {availableCities.length > 0 && (
                    <Box display="flex" gap={1}>
                      <GlobalButton
                        variant="tertiary"
                        size="small"
                        disabled={
                          availableCities.length === 0 ||
                          availableCities.every((c) =>
                            linkedCityNames.includes(c),
                          )
                        }
                        onClick={() => {
                          const toAdd = availableCities.filter(
                            (c) => !linkedCityNames.includes(c),
                          );
                          // Geocode all newly-added cities that still need it
                          toAdd.forEach((cityName) => {
                            if (!resolvedCoords[cityName]) {
                              geocodeCity(cityName, formData.stateName);
                            }
                          });
                          setLinkedCityNames((prev) => [
                            ...new Set([...prev, ...toAdd]),
                          ]);
                          if (submitError) setSubmitError(null);
                        }}
                      >
                        Select All
                      </GlobalButton>
                      <GlobalButton
                        variant="tertiary"
                        size="small"
                        disabled={linkedCityNames.length === 0}
                        onClick={() => {
                          setLinkedCityNames((prev) =>
                            prev.filter((c) => !availableCities.includes(c)),
                          );
                          if (submitError) setSubmitError(null);
                        }}
                      >
                        Clear All
                      </GlobalButton>
                    </Box>
                  )}
                </Box>

                {cityLoading ? (
                  <Box
                    display="flex"
                    alignItems="center"
                    gap={1}
                    color="text.secondary"
                  >
                    <CircularProgress size={16} />
                    <Typography variant="body2">Loading cities…</Typography>
                  </Box>
                ) : availableCities.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    {formData.stateName
                      ? search
                        ? "No cities match your search."
                        : "No additional cities available in this state."
                      : "Select a state to see available cities."}
                  </Typography>
                ) : (
                  <Grid
                    container
                    spacing={0.5}
                    sx={{ maxHeight: 280, overflowY: "auto", pr: 1 }}
                  >
                    {availableCities.map((city) => {
                      const isSelected = linkedCityNames.includes(city);
                      const isGeocoding =
                        isSelected && geocodingRef.current.has(city);
                      const hasPin =
                        isSelected && Boolean(resolvedCoords[city]);
                      return (
                        <Grid item xs={12} sm={6} md={4} key={city}>
                          <Box
                            display="flex"
                            alignItems="center"
                            sx={{
                              borderRadius: 1,
                              px: 0.5,
                              cursor: "pointer",
                              "&:hover": { bgcolor: "action.hover" },
                            }}
                            onClick={() => {
                              toggleCity(city);
                              if (submitError) setSubmitError(null);
                            }}
                          >
                            <Checkbox
                              size="small"
                              checked={isSelected}
                              onChange={() => {
                                toggleCity(city);
                                if (submitError) setSubmitError(null);
                              }}
                              onClick={(e) => e.stopPropagation()}
                            />
                            <Typography
                              variant="body2"
                              noWrap
                              title={city}
                              sx={{ flex: 1 }}
                            >
                              {city}
                            </Typography>
                            {/* Small pin-state indicator next to each selected city */}
                            {isSelected && (
                              <Box
                                sx={{
                                  ml: 0.5,
                                  display: "flex",
                                  alignItems: "center",
                                }}
                              >
                                {isGeocoding ? (
                                  <CircularProgress size={10} thickness={5} />
                                ) : (
                                  <Box
                                    sx={{
                                      width: 8,
                                      height: 8,
                                      borderRadius: "50%",
                                      // Green = pin on map, grey = geocoding failed/unavailable
                                      bgcolor: hasPin
                                        ? "#D32F2F"
                                        : "text.disabled",
                                    }}
                                    title={
                                      hasPin
                                        ? "Pinned on map"
                                        : "Location unavailable"
                                    }
                                  />
                                )}
                              </Box>
                            )}
                          </Box>
                        </Grid>
                      );
                    })}
                  </Grid>
                )}
              </Box>
            </Paper>
          </Grid>

          {/* Right: Map */}
          <Grid item xs={12} md={5}>
            <Paper
              variant="outlined"
              sx={{ p: 2, borderRadius: 3, height: "100%" }}
            >
              {/* Map header with live geocoding indicator */}
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                mb={1}
              >
                <Box display="flex" alignItems="center" gap={1}>
                  <Typography variant="h6" fontWeight={700}>
                    Map Preview
                  </Typography>
                  {pendingCount > 0 && (
                    <Box display="flex" alignItems="center" gap={0.5}>
                      <CircularProgress size={13} thickness={5} />
                      <Typography variant="caption" color="text.secondary">
                        Locating {pendingCount}{" "}
                        {pendingCount === 1 ? "city" : "cities"}…
                      </Typography>
                    </Box>
                  )}
                </Box>
                {/* Legend */}
                <Box display="flex" gap={2}>
                  <Box display="flex" alignItems="center" gap={0.5}>
                    <Box
                      sx={{
                        width: 12,
                        height: 12,
                        borderRadius: "50%",
                        bgcolor: "#1565C0",
                        border: "1.5px solid #fff",
                        boxShadow: "0 0 0 1px #1565C0",
                      }}
                    />
                    <Typography variant="caption">Delivery Center</Typography>
                  </Box>
                  <Box display="flex" alignItems="center" gap={0.5}>
                    <Box
                      sx={{
                        width: 12,
                        height: 12,
                        borderRadius: "50%",
                        bgcolor: "#D32F2F",
                        border: "1.5px solid #fff",
                        boxShadow: "0 0 0 1px #D32F2F",
                      }}
                    />
                    <Typography variant="caption">Linked City</Typography>
                  </Box>
                </Box>
              </Box>

              {!isLoaded ? (
                <Box
                  display="flex"
                  justifyContent="center"
                  alignItems="center"
                  height={450}
                  gap={1}
                >
                  <CircularProgress size={24} />
                  <Typography variant="body2" color="text.secondary">
                    Loading map…
                  </Typography>
                </Box>
              ) : (
                <>
                  <Box mb={2}>
                    <Typography variant="body2" gutterBottom>
                      Auto Select Radius: {radiusKm} km
                    </Typography>

                    <Slider
                      value={radiusKm}
                      min={10}
                      max={500}
                      step={10}
                      valueLabelDisplay="auto"
                      onChange={handleRadiusChange}
                    />
                  </Box>
                  <GoogleMap
                    mapContainerStyle={MAP_CONTAINER_STYLE}
                    zoom={mapMarkers.length > 0 ? 7 : 5}
                    center={mapCenter}
                    options={{
                      mapTypeControl: false,
                      streetViewControl: false,
                    }}
                    onClick={() => setSelectedMarker(null)}
                  >
                    {mapMarkers.map((m) => (
                      <Marker
                        key={`${m.name}-${m.isDC ? "dc" : "linked"}`}
                        position={{ lat: m.lat, lng: m.lng }}
                        icon={makeIcon(m.isDC)}
                        title={m.isDC ? `📍 DC: ${m.name}` : m.name}
                        onClick={() => setSelectedMarker(m)}
                      />
                    ))}
                    {resolvedCoords[formData.cityName] && (
                      <Circle
                        center={resolvedCoords[formData.cityName]}
                        radius={radiusKm * 1000}
                        options={{
                          fillOpacity: 0.15,
                          strokeOpacity: 0.8,
                          strokeWeight: 2,
                          clickable: false,
                          draggable: false,
                          editable: false,
                        }}
                      />
                    )}
                    {selectedMarker && (
                      <InfoWindow
                        position={{
                          lat: selectedMarker.lat,
                          lng: selectedMarker.lng,
                        }}
                        onCloseClick={() => setSelectedMarker(null)}
                      >
                        <Box sx={{ p: 0.5, minWidth: 140 }}>
                          <Typography
                            variant="subtitle2"
                            fontWeight={700}
                            color={selectedMarker.isDC ? "primary" : "error"}
                          >
                            {selectedMarker.isDC
                              ? "🏭 Delivery Center"
                              : "🏙️ Linked City"}
                          </Typography>
                          <Typography variant="body2" mt={0.5}>
                            {selectedMarker.name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {selectedMarker.lat.toFixed(4)},{" "}
                            {selectedMarker.lng.toFixed(4)}
                          </Typography>
                        </Box>
                      </InfoWindow>
                    )}
                  </GoogleMap>
                </>
              )}
            </Paper>
          </Grid>
        </Grid>

        <Box display="flex" justifyContent="flex-end" gap={2} mt={4}>
          <div className="w-30">
            <GlobalButton
              variant="secondary"
              size="medium"
              onClick={() => {
                resetModalState();
                onClose();
              }}
              disabled={isSavingState}
            >
              Cancel
            </GlobalButton>
          </div>
          <div className="w-30">
            <GlobalButton
              variant="primary"
              size="medium"
              onClick={handleSubmit}
              disabled={isSavingState}
              isLoading={isSavingState}
            >
              {isEditMode ? "Update" : "Create"}
            </GlobalButton>
          </div>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default ViewDeliveryCenterModal;
const getDistanceKm = (lat1, lon1, lat2, lon2) => {
  const R = 6371;

  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLon = ((lon2 - lon1) * Math.PI) / 180;

  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos((lat1 * Math.PI) / 180) *
      Math.cos((lat2 * Math.PI) / 180) *
      Math.sin(dLon / 2) *
      Math.sin(dLon / 2);

  return R * (2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a)));
};
